import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { projectsApi, type ProjectRole, type InviteRole, type Member } from "@/api/projects";
import { Dialog } from "@/components/ui/Dialog";
import { useAuthStore } from "@/auth/store";
import { Plus, Trash2 } from "lucide-react";

export const Route = createFileRoute("/app/projects/$pid/members")({
  component: MembersPage,
});

const ROLES: ProjectRole[] = ["owner", "developer", "billing"];
// "owner" only available to callers who are themselves owner — server
// enforces; FE filters to keep the dropdown honest.
const INVITE_ROLES_OWNER: InviteRole[]   = ["owner", "developer", "billing"];
const INVITE_ROLES_DEFAULT: InviteRole[] = ["developer", "billing"];

function MembersPage() {
  const { pid } = Route.useParams();
  const qc = useQueryClient();
  // Used to gate self-modification controls: a user can't change their
  // own role or remove themselves (server enforces this too — defense
  // in depth here is just to avoid confusing UX).
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data: members, isLoading } = useQuery({
    queryKey: ["members", pid], queryFn: () => projectsApi.listMembers(pid),
  });
  const { data: invites } = useQuery({
    queryKey: ["invites", pid], queryFn: () => projectsApi.listInvites(pid),
  });
  const [inviteOpen, setInviteOpen] = useState(false);

  const setRole = useMutation({
    mutationFn: ({ uid, role }: { uid: string; role: ProjectRole }) =>
      projectsApi.setMemberRole(pid, uid, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", pid] }),
  });
  const remove = useMutation({
    mutationFn: (uid: string) => projectsApi.removeMember(pid, uid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["members", pid] }),
  });
  const revokeInv = useMutation({
    mutationFn: (id: string) => projectsApi.revokeInvite(pid, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invites", pid] }),
  });
  const resendInv = useMutation({
    mutationFn: (id: string) => projectsApi.resendInvite(pid, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invites", pid] }),
  });

  return (
    <div className="space-y-8">
      <div className="flex justify-end">
        <button className="btn-primary" onClick={() => setInviteOpen(true)}>
          <Plus size={16} className="mr-1" /> Invite member
        </button>
      </div>

      <section>
        <h3 className="text-sm font-medium text-ink/75">Members</h3>
        <div className="card mt-3 overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? <tr><td colSpan={4} className="px-4 py-6 text-ink/55">Loading…</td></tr>
              : members?.map((m: Member) => {
                const isSelf = m.user_id === currentUserId;
                return (
                <tr key={m.user_id} className="border-t border-ink/8">
                  <td className="px-4 py-3">
                    {m.email}
                    {isSelf && <span className="ml-2 text-xs text-ink/55">(you)</span>}
                  </td>
                  <td className="px-4 py-3">
                    {isSelf ? (
                      <span className="text-sm text-ink/75">{m.role}</span>
                    ) : (
                      <select
                        value={m.role}
                        onChange={(e) => setRole.mutate({ uid: m.user_id, role: e.target.value as ProjectRole })}
                        className="rounded-md border border-ink/15 px-2 py-1 text-sm"
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink/55">{new Date(m.joined_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    {!isSelf && (
                      <button
                        onClick={() => { if (confirm(`Remove ${m.email}?`)) remove.mutate(m.user_id); }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {invites && invites.length > 0 && (
        <section>
          <h3 className="text-sm font-medium text-ink/75">Pending invites</h3>
          <div className="card mt-3 overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Expires</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {invites
                  .filter((i) => !i.accepted_at)
                  .map((i) => (
                    <tr key={i.id} className="border-t border-ink/8">
                      <td className="px-4 py-3">{i.email}</td>
                      <td className="px-4 py-3 text-ink/65">{i.role}</td>
                      <td className="px-4 py-3 text-ink/55">{new Date(i.expires_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => resendInv.mutate(i.id)}
                          disabled={resendInv.isPending}
                          className="mr-3 text-sm text-primary-600 hover:text-primary-800 disabled:text-ink/35"
                        >Resend</button>
                        <button
                          onClick={() => revokeInv.mutate(i.id)}
                          className="text-sm text-red-600 hover:text-red-800"
                        >Revoke</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <InviteDialog
        pid={pid}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        callerRole={members?.find((m: Member) => m.user_id === currentUserId)?.role ?? null}
      />
    </div>
  );
}

function InviteDialog({
  pid, open, onOpenChange, callerRole,
}: {
  pid: string; open: boolean; onOpenChange: (b: boolean) => void;
  callerRole: ProjectRole | null;
}) {
  const inviteRoles = callerRole === "owner" ? INVITE_ROLES_OWNER : INVITE_ROLES_DEFAULT;
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteRole>("developer");
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => projectsApi.invite(pid, email.trim().toLowerCase(), role),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["invites", pid] });
      onOpenChange(false);
      setEmail("");
    },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Invite member">
      <form onSubmit={(e) => { e.preventDefault(); if (email.trim()) m.mutate(); }} className="space-y-4">
        <div>
          <label className="label">Email</label>
          <input type="email" className="input mt-1" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="label">Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value as InviteRole)} className="input mt-1">
            {inviteRoles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => onOpenChange(false)} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary" disabled={m.isPending}>{m.isPending ? "Sending…" : "Send invite"}</button>
        </div>
      </form>
    </Dialog>
  );
}
